import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { closeDetailDrawer, showToast } from '@/features/ui/uiSlice';
import { togglePlay, setPlaying, seekToSeconds, updateCurrentTime, setTotalSeconds } from '@/features/player/playerSlice';
import { fetchAssetTags, addAssetTag, deleteAssetTag } from '@/features/tags/tagsSlice';
import ShareModal from '@/components/ui/ShareModal/ShareModal';
import DownloadModal from '@/components/ui/DownloadModal/DownloadModal';
import { formatTimestamp, msToTimestamp, parseTimestampInput, toDisplayText, formatAttrsTooltip } from '@/utils/formatters';
import { TAG_CATEGORIES } from '@/config/tagCategories';
import { MOCK_CAMPAIGNS, OPEN_IN_APPS } from '@/config/campaigns';
import { shouldStopAtBoundary } from '@/utils/clipBoundary';
import { setClipEndBoundary } from '@/features/player/playerSlice';
import type { TagOverride, TagType, VideoCategory } from '@/types';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import demoVideo from '@/assets/videos/Image_from_iOS.MOV';
import styles from './AssetDetailDrawer.module.css';

const CATEGORY_LABEL: Record<VideoCategory, string> = {
  testimonial: 'Testimonial',
  social: 'Social',
  event: 'Event',
  internal: 'Internal',
  recruit: 'Recruiting',
  training: 'Training',
  highlight: 'Highlight',
};

// Same categories/labels as the hero section's Insights tab — TAG_CATEGORIES
// is shared (src/config/tagCategories.ts) so the two surfaces can't drift.

// Below this viewport width, the expanded layout (fixed 760px video column +
// description column + gaps/padding, inside an 80vw sheet) can't actually fit
// side-by-side without squeezing the description column illegibly — so the
// expand feature itself is hidden rather than offering a state that looks
// broken once entered.
const MIN_WIDTH_FOR_EXPAND = 1400;

// Matches .chipRowClamped's max-height in AssetDetailDrawer.module.css. Used
// as a fixed comparison for the "See more…" button's offsetTop — NOT the
// row's own clientHeight, which shrinks below this ceiling once there's
// less content than 2 full lines (max-height is only an upper bound), which
// would make an offsetTop-vs-clientHeight comparison chase a moving target.
const CHIP_ROW_CLAMP_PX = 60;

export default function AssetDetailDrawer() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.detailDrawerOpen);
  const item = useAppSelector((s) => s.ui.detailDrawerItem);
  const activeAsset = useAppSelector((s) => s.search.activeAsset);
  const loadingAssetId = useAppSelector((s) => s.search.loadingAssetId);
  const isPlaying = useAppSelector((s) => s.player.isPlaying);
  const currentSeconds = useAppSelector((s) => s.player.currentSeconds);
  const activeClipEndSeconds = useAppSelector((s) => s.player.activeClipEndSeconds);
  const assetTags = useAppSelector((s) => (item ? s.tags.assetTags[item.id] : undefined));
  const tagsLoadingId = useAppSelector((s) => s.tags.loadingAssetId);
  const addLoading = useAppSelector((s) => s.tags.addLoading);
  const deleteLoading = useAppSelector((s) => s.tags.deleteLoading);

  // Hide the expand feature (and drop back out of it) once the window
  // shrinks below the width the expanded layout actually needs.
  useEffect(() => {
    const onResize = () => {
      const fits = window.innerWidth >= MIN_WIDTH_FOR_EXPAND;
      setCanExpand(fits);
      if (!fits) setExpanded(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const videoRef = useRef<HTMLVideoElement>(null);
  // Tracks the latest isPlaying without being a dependency of the
  // reset-on-new-item effect below (that effect must not re-run on every
  // play/pause toggle — only once, right when a new item opens).
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const [started, setStarted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(() => window.innerWidth >= MIN_WIDTH_FOR_EXPAND);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [contentSearch, setContentSearch] = useState('');
  const [noteText, setNoteText] = useState('');
  const [addingCategory, setAddingCategory] = useState<TagType | null>(null);
  const [addValue, setAddValue] = useState('');
  const [addStartMs, setAddStartMs] = useState('');
  const [editingTag, setEditingTag] = useState<{ key: string; type: TagType; tag: TagOverride } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editStartMs, setEditStartMs] = useState('');
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const [insightsOpen, setInsightsOpen] = useState(true);
  // Per-category accordion state for the tag chips inside Insights — missing
  // key = expanded (so newly-appearing categories default open).
  const [openCategories, setOpenCategories] = useState<Partial<Record<TagType, boolean>>>({});
  const isCategoryOpen = (type: TagType) => openCategories[type] ?? true;
  const toggleCategory = (type: TagType) => {
    const next = !isCategoryOpen(type);
    trackEvent(ANALYTICS_EVENTS.ACCORDION_TOGGLE, { section: `insights_${type}`, open: next });
    setOpenCategories((prev) => ({ ...prev, [type]: next }));
  };
  // Tag chips clamp to 2 lines per category until "See more…" is clicked.
  // `tagsOverflow` (measured from the DOM after render) controls whether the
  // button shows at all — categories that already fit in 2 lines never get one.
  const [tagsSeeMore, setTagsSeeMore] = useState<Partial<Record<TagType, boolean>>>({});
  const [tagsOverflow, setTagsOverflow] = useState<Partial<Record<TagType, boolean>>>({});
  const chipRowRefs = useRef<Partial<Record<TagType, HTMLDivElement | null>>>({});
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false);
  const [openInMenuOpen, setOpenInMenuOpen] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [campaigns, setCampaigns] = useState(MOCK_CAMPAIGNS);

  const sendToRef = useRef<HTMLDivElement>(null);

  // Reset transient UI state and (re)load tags whenever a new asset opens.
  // `started` picks up whatever isPlaying already is — callers that want the
  // drawer to open straight into playback (e.g. search results) set isPlaying
  // true via seekToSeconds before dispatching openDetailDrawer; callers that
  // want the paused thumbnail (e.g. the library grid) leave it false.
  useEffect(() => {
    if (!item) return;
    setStarted(isPlayingRef.current);
    setContentSearch('');
    setNoteText('');
    setAddingCategory(null);
    setAddValue('');
    setAddStartMs('');
    setEditingTag(null);
    setEditStartMs('');
    setTranscriptOpen(true);
    setInsightsOpen(true);
    setOpenCategories({});
    setTagsSeeMore({});
    setTagsOverflow({});
    setExpanded(false);
    setCampaignMenuOpen(false);
    setOpenInMenuOpen(false);
    setNewCampaignName('');
    dispatch(fetchAssetTags(item.id));
  }, [item?.id, dispatch]);

  // Close the "Add to campaign" / "Open in…" dropdowns on outside click.
  useEffect(() => {
    if (!campaignMenuOpen && !openInMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (sendToRef.current && !sendToRef.current.contains(e.target as Node)) {
        setCampaignMenuOpen(false);
        setOpenInMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [campaignMenuOpen, openInMenuOpen]);


  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') dispatch(closeDetailDrawer()); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, dispatch]);

  // Lock background page scroll while the drawer is open. Plain
  // `overflow: hidden` on <body> snaps the page to scrollY 0 in Chromium and
  // never restores it, so instead pin the body at its current scroll offset
  // (the standard scroll-lock trick) and undo it on close.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Keep the drawer's own video element in sync with the shared player state
  // (the same store TranscriptPanel's "click a sentence to jump" uses).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !started) return;
    if (isPlaying) video.play().catch(() => dispatch(setPlaying(false)));
    else video.pause();
  }, [isPlaying, started, dispatch]);

  // Seek the mounted element to the shared player position. Some assets aren't
  // seekable the instant we set currentTime — their metadata (duration / seek
  // index) loads slower, or the source is served as a single 200 without HTTP
  // range support. Assigning currentTime while the element is still in
  // HAVE_NOTHING / not-yet-seekable state is silently dropped, so playback
  // stays at 0 and clicking a tag/transcript appears to "restart from zero".
  // Fix: apply the seek as soon as the target actually falls inside a seekable
  // range, retrying as metadata arrives and the buffer grows.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !started) return;
    const target = currentSeconds;
    if (Number.isNaN(target) || Math.abs(video.currentTime - target) <= 0.5) return;

    let applied = false;
    const targetIsSeekable = () => {
      const ranges = video.seekable;
      for (let i = 0; i < ranges.length; i++) {
        if (target >= ranges.start(i) - 0.25 && target <= ranges.end(i) + 0.25) return true;
      }
      return false;
    };
    const attempt = () => {
      if (applied || video.readyState < 1 /* HAVE_METADATA */ || !targetIsSeekable()) return;
      video.currentTime = target;
      applied = true;
      detach();
    };
    const detach = () => {
      video.removeEventListener('loadedmetadata', attempt);
      video.removeEventListener('durationchange', attempt);
      video.removeEventListener('canplay', attempt);
      video.removeEventListener('progress', attempt);
    };

    attempt();
    if (applied) return;
    // Not seekable yet — retry as metadata loads and more data buffers in.
    // `durationchange` specifically covers non-faststart files whose moov atom
    // (and thus the seek index) is only parsed once the file finishes buffering.
    video.addEventListener('loadedmetadata', attempt);
    video.addEventListener('durationchange', attempt);
    video.addEventListener('canplay', attempt);
    video.addEventListener('progress', attempt);
    return detach;
  }, [currentSeconds, started]);

  // Must run before the early return below (hooks can't be conditional).
  const activeLineIndex = useMemo(() => {
    const lines = (item && activeAsset?.asset_id === item.id ? activeAsset?.transcript : null) ?? [];
    let found = -1;
    lines.forEach((line, i) => {
      if (line.start_ms / 1000 <= currentSeconds) found = i;
    });
    return found;
  }, [item, activeAsset, currentSeconds]);

  // Measure each category's chip row after render to decide (a) whether it
  // actually overflows 2 lines at all — categories that already fit never
  // get a "See more…" button — and (b) if so, which trailing tags need to
  // stay hidden so the button (rendered as the last item inside the
  // clamped row, so it wraps at the end of the visible tags rather than
  // always dropping to its own row underneath) lands inside the clamp
  // instead of on a clipped, invisible 3rd line.
  //
  // The row always renders every tag (see the .map() below) — which ones
  // stay hidden while collapsed is applied here imperatively (inline
  // `display: none`), not via React state driving which tags get rendered.
  // An earlier version instead sliced the rendered array by a `chipTrim`
  // count in React state: correct in isolation, but not idempotent — this
  // effect can end up re-firing for reasons unrelated to the tag content
  // itself (e.g. a dependency like `openCategories` getting a fresh `{}`
  // reference from React 18 StrictMode's dev-only double-invoked effects
  // elsewhere in this component), and on a re-fire it would measure
  // whatever was CURRENTLY rendered — i.e. the already-trimmed row from the
  // previous pass — rather than the true full set, concluding it already
  // fits and resetting the trim to 0, which promptly un-hides everything
  // and removes the button. Confirmed to actually happen in practice.
  // Resetting every chip to visible first, on every single invocation,
  // before measuring, makes this fully self-correcting regardless of how
  // many times (or why) it re-runs.
  useLayoutEffect(() => {
    const nextOverflow: Partial<Record<TagType, boolean>> = {};
    TAG_CATEGORIES.forEach(({ type }) => {
      const row = chipRowRefs.current[type];
      if (!row) return;
      const chipNodes = Array.from(row.children).filter((n) => n.tagName !== 'BUTTON') as HTMLElement[];
      chipNodes.forEach((n) => { n.style.display = ''; });
      row.style.maxHeight = '';

      if (tagsSeeMore[type]) {
        // Expanded — nothing clamped, nothing to hide, but the toggle
        // button must stay (now reading "See less") so it's still possible
        // to collapse back; only reachable via "See more…" in the first
        // place, so it was already known to overflow.
        nextOverflow[type] = true;
        return;
      }

      const overflow = row.scrollHeight > row.clientHeight + 1;
      nextOverflow[type] = overflow;
      if (!overflow) return;

      // Hide only the tags that don't fit within the 2-line clamp on their
      // own — never sacrifice one just to make room for the button.
      for (let i = chipNodes.length - 1; i >= 0; i--) {
        const n = chipNodes[i];
        if (n.offsetTop + n.offsetHeight <= CHIP_ROW_CLAMP_PX + 1) break;
        n.style.display = 'none';
      }

      // See where the button lands after that: inline at the end of the
      // last visible line if there's room, otherwise it wraps to its own
      // next line — in which case the clamp grows by exactly that much so
      // it isn't clipped, rather than trimming another tag to force it
      // inline.
      const probe = document.createElement('button');
      probe.className = styles.seeMoreBtn;
      probe.textContent = 'See more…';
      row.appendChild(probe);
      const probeBottom = probe.offsetTop + probe.offsetHeight;
      if (probeBottom > CHIP_ROW_CLAMP_PX + 1) {
        row.style.maxHeight = `${probeBottom}px`;
      }
      row.removeChild(probe);
    });
    setTagsOverflow(nextOverflow);
  }, [assetTags, contentSearch, insightsOpen, openCategories, tagsSeeMore]);

  // Freeze the first non-empty playback URL per asset id (see the block that
  // uses this below the early return). Declared here — above the early return —
  // so the hook count stays stable when the drawer closes (open/item null).
  const frozenPlaybackRef = useRef<{ id: string; url: string } | null>(null);

  if (!open || !item) return null;

  const isMatchingAsset = activeAsset?.asset_id === item.id;
  const detail = isMatchingAsset ? activeAsset : null;
  const detailLoading = loadingAssetId === item.id;
  // Fall back to the tile's own contentType before detail has loaded, so the
  // Transcript column never flashes in for an image asset.
  const isImageAsset = (detail?.content_type ?? item.contentType) === 'image';
  const tagsLoading = tagsLoadingId === item.id && !assetTags;

  const rawSummary = detail?.summary ?? detail?.all_sections?.scenes?.[0] ?? null;
  const summary = rawSummary != null ? toDisplayText(rawSummary) : null;
  const matchConfidence = detail?.matched_segments?.[0]?.match_score;

  const storageUrl = detail?.storage_url ?? item.storage_url;
  const downloadUrl = item.download_url || storageUrl || '';

  // The <video> src must stay STABLE while an asset is open. Both the grid feed
  // and fetchStorageAsset (getAsset) return freshly-signed URLs for the SAME
  // blob, so `storageUrl` flips to a different string the moment the detail
  // fetch resolves. If that happens while the element is mounted, it swaps the
  // src and re-downloads the same file — blocking the in-flight request and
  // delaying playback. So freeze the first non-empty URL per asset id and use
  // that for playback; `storageUrl` (fresh) still drives download/share/copy.
  if (frozenPlaybackRef.current?.id !== item.id) frozenPlaybackRef.current = null;
  if (!frozenPlaybackRef.current && storageUrl) {
    frozenPlaybackRef.current = { id: item.id, url: storageUrl };
  }
  const playbackUrl = frozenPlaybackRef.current?.url ?? storageUrl;

  const searchLower = contentSearch.trim().toLowerCase();

  const transcriptLines = detail?.transcript ?? [];
  const filteredLines = searchLower
    ? transcriptLines.filter((l) =>
        l.text.toLowerCase().includes(searchLower) ||
        l.speaker.toLowerCase().includes(searchLower)
      )
    : transcriptLines;

  const notes = assetTags?.tags?.descriptions ?? [];

  const handleSeekLine = (seconds: number) => {
    trackEvent(ANALYTICS_EVENTS.VIDEO_SEEK_TRANSCRIPT, { assetId: item?.id, seconds });
    setStarted(true);
    // Manually jumping to a transcript line/tag means the user wants to keep
    // playing past that point — clear any leftover clip-end boundary from a
    // previous search-result match, or playback would immediately halt the
    // instant it reached that stale cutoff.
    dispatch(setClipEndBoundary(null));
    dispatch(seekToSeconds(seconds));
  };

  const handleAddTag = (type: TagType) => {
    if (!item) return;
    const trimmed = addValue.trim();
    if (!trimmed) return;
    const startMs = parseTimestampInput(addStartMs);
    trackEvent(ANALYTICS_EVENTS.TAG_ADD, { assetId: item.id, tagType: type });
    dispatch(addAssetTag({
      assetId: item.id,
      payload: {
        tag_type: type, human_value: trimmed, source: 'manual',
        ...(startMs != null && { timestamp_ms_start: startMs }),
      },
    })).then(() => dispatch(fetchAssetTags(item.id)));
    setAddValue('');
    setAddStartMs('');
    setAddingCategory(null);
  };

  const startEditTag = (key: string, type: TagType, tag: TagOverride) => {
    setAddingCategory(null);
    setEditingTag({ key, type, tag });
    setEditValue(toDisplayText(tag.text));
    setEditStartMs(tag.timestamp_ms != null ? msToTimestamp(tag.timestamp_ms) : msToTimestamp(currentSeconds * 1000));
  };

  const handleEditTagSave = () => {
    if (!editingTag || !item) return;
    const { tag, type } = editingTag;
    const trimmed = editValue.trim();
    const startMs = parseTimestampInput(editStartMs);
    const textChanged = trimmed !== toDisplayText(tag.text);
    const tsChanged = startMs !== tag.timestamp_ms;
    setEditingTag(null);
    if (!trimmed || (!textChanged && !tsChanged)) return;
    trackEvent(ANALYTICS_EVENTS.TAG_EDIT, { assetId: item.id, tagType: type });
    (async () => {
      if (tag.id) await dispatch(deleteAssetTag({ assetId: item.id, tagId: tag.id }));
      await dispatch(addAssetTag({
        assetId: item.id,
        payload: {
          tag_type: type, human_value: trimmed, ai_value: tag.ai_value ?? null, source: 'human',
          ...(startMs != null && { timestamp_ms_start: startMs }),
        },
      }));
      dispatch(fetchAssetTags(item.id));
    })();
  };

  const handleAddNote = () => {
    if (!item) return;
    const trimmed = noteText.trim();
    if (!trimmed) return;
    trackEvent(ANALYTICS_EVENTS.NOTE_ADD, { assetId: item.id });
    dispatch(addAssetTag({ assetId: item.id, payload: { tag_type: 'description', human_value: trimmed, source: 'manual' } }))
      .then(() => dispatch(fetchAssetTags(item.id)));
    setNoteText('');
  };

  const handleHeroPlay = () => {
    trackEvent(ANALYTICS_EVENTS.VIDEO_PLAY, { assetId: item?.id, source: 'hero' });
    setStarted(true);
    dispatch(seekToSeconds(0));
  };

  const handleFootPlay = () => {
    if (!started) { handleHeroPlay(); return; }
    trackEvent(isPlaying ? ANALYTICS_EVENTS.VIDEO_PAUSE : ANALYTICS_EVENTS.VIDEO_PLAY, { assetId: item?.id, source: 'footer' });
    dispatch(togglePlay());
  };

  const handleCopyLink = async () => {
    if (!storageUrl) return;
    trackEvent(ANALYTICS_EVENTS.COPY_LINK, { assetId: item?.id });
    try {
      await navigator.clipboard.writeText(storageUrl);
    } catch {
      // ignore — clipboard access may be blocked
    }
    dispatch(showToast({ title: 'Link copied', message: 'The clip link has been copied to your clipboard.' }));
  };

  const handleAssignCampaign = (campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (campaign) {
      trackEvent(ANALYTICS_EVENTS.CAMPAIGN_ASSIGN, { assetId: item?.id, campaignId });
      dispatch(showToast({ title: 'Added to campaign', message: `Asset added to "${campaign.label}".` }));
    }
    setCampaignMenuOpen(false);
  };

  const handleCreateCampaign = () => {
    const name = newCampaignName.trim();
    if (!name) return;
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_CREATE, { assetId: item?.id, name });
    setCampaigns((prev) => [
      ...prev,
      { id: `custom-${prev.length}-${name}`, label: name, avatar: name.charAt(0).toUpperCase(), color: '#2E7BF6', assetCount: 1, status: 'In production' },
    ]);
    dispatch(showToast({ title: 'Campaign created', message: `"${name}" created and asset added.` }));
    setNewCampaignName('');
    setCampaignMenuOpen(false);
  };

  const handleOpenInApp = (label: string) => {
    trackEvent(ANALYTICS_EVENTS.OPEN_IN_APP, { assetId: item?.id, app: label });
    dispatch(showToast({ title: 'Opening…', message: `Opening this clip in ${label}.` }));
    setOpenInMenuOpen(false);
  };

  // Title/byline/AI-summary/matched-entities — rendered in one of two spots
  // depending on `expanded`: pinned beside the video when expanded, or
  // inline at the top of the scrollable body (so it scrolls with the rest
  // of the content) when collapsed.
  const descriptionBlock = (
    <>
      {/* Title row — video name and Share clip on the same level. When
          expanded, Share clip moves below the video (on its right side)
          instead, so it's omitted here to avoid showing it twice. */}
      <div className={styles.titleRow}>
        <h2 className={styles.title}>{item.title}</h2>
        {!expanded && (
          <button className={styles.shareIconBtn} onClick={() => { trackEvent(ANALYTICS_EVENTS.SHARE_CLICK, { assetId: item.id }); setShareOpen(true); }} aria-label={isImageAsset ? 'Share' : 'Share clip'} title={isImageAsset ? 'Share' : 'Share clip'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
              <line x1="8.6" y1="10.6" x2="15.4" y2="6.4" /><line x1="8.6" y1="13.4" x2="15.4" y2="17.6" />
            </svg>
            {isImageAsset ? 'Share' : 'Share clip'}
          </button>
        )}
      </div>
      <div className={styles.byline}>
        {item.creator && <span>{item.creator}</span>}
        {item.creator && (item.timestamp || item.meta) && <span className={styles.bylineDot}>·</span>}
        <span>{item.timestamp || item.meta}</span>
      </div>

      {/* AI Scene Analysis */}
      {detailLoading ? (
        <div className={styles.skelLine} style={{ width: '40%', height: '20px', marginBottom: '10px' }} />
      ) : null}
      {detailLoading ? (
        <>
          <div className={styles.skelLine} style={{ width: '100%' }} />
          <div className={styles.skelLine} style={{ width: '90%' }} />
          <div className={styles.skelLine} style={{ width: '60%' }} />
        </>
      ) : summary ? (
        <>
          <div className={styles.aiBadgeRow}>
            <span className={styles.aiBadge}>
              <span className={styles.aiPulse} />
              AI Scene Analysis
            </span>
            {matchConfidence != null && (
              <span className={styles.aiConfidence}>{Math.round(matchConfidence)}% confidence</span>
            )}
          </div>
          {/* Only this text scrolls internally when expanded (see
              .summaryScroll) — title/byline above and the matched-entities
              chips below always stay fully visible. */}
          <div className={styles.summaryScroll}>
            <p className={styles.summary}>{summary}</p>
          </div>
        </>
      ) : null}

      {/* Matched entities — "why it matched", same data source as the hero Insights tab */}
      {detailLoading ? (
        <>
          <div className={styles.sectLabel}>Recognized in this clip</div>
          <div className={styles.chipRow}>
            <span className={styles.skelChip} />
            <span className={styles.skelChip} style={{ width: '96px' }} />
            <span className={styles.skelChip} style={{ width: '60px' }} />
          </div>
        </>
      ) : (detail?.matched_entities?.length ?? 0) > 0 && (
        <>
          <div className={styles.sectLabel}>Recognized in this clip</div>
          <div className={styles.chipRow}>
            {detail!.matched_entities!.map((entity, i) => {
              const tooltip = formatAttrsTooltip(entity.attrs);
              const seekMs = entity.first_seen_ms;
              return (
                <span
                  key={`${entity.kind}-${entity.label}-${i}`}
                  className={`${styles.tagChip}${seekMs != null ? ` ${styles.tagChipClickable}` : ''}`}
                  title={tooltip ?? undefined}
                  onClick={seekMs != null ? () => { trackEvent(ANALYTICS_EVENTS.TAG_CLICK, { assetId: item.id, tagType: entity.kind, timestamp_ms: seekMs }); handleSeekLine(seekMs / 1000); } : undefined}
                  role={seekMs != null ? 'button' : undefined}
                >
                  {entity.kind === 'person' ? 'Person' : 'Object'}: {entity.label}
                  {tooltip && <span className={styles.tagChipAttrs} title={tooltip}>ⓘ</span>}
                </span>
              );
            })}
          </div>
        </>
      )}
    </>
  );

  // Search bar (+ Share clip, expanded only) — pinned beside the video/
  // description when expanded (never scrolls), inline at the top of the
  // scrollable body when collapsed.
  const searchRowBlock = (
    <div className={expanded ? styles.searchRowExpanded : undefined}>
      {detailLoading || tagsLoading ? (
        <div className={styles.skelLine} style={{ width: expanded ? '420px' : '100%', height: '32px', borderRadius: '9px', marginTop: '20px', marginBottom: '8px' }} />
      ) : (
        <div className={`${styles.transcriptSearch} ${expanded ? styles.transcriptSearchExpanded : ''}`}>
          <svg className={styles.transcriptSearchIcon} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
          </svg>
          <input
            className={styles.transcriptSearchInput}
            placeholder={isImageAsset ? 'Search tags…' : 'Search transcript and tags…'}
            value={contentSearch}
            onChange={(e) => setContentSearch(e.target.value)}
          />
        </div>
      )}
      {expanded && (
        <button className={styles.shareIconBtn} onClick={() => { trackEvent(ANALYTICS_EVENTS.SHARE_CLICK, { assetId: item.id }); setShareOpen(true); }} aria-label={isImageAsset ? 'Share' : 'Share clip'} title={isImageAsset ? 'Share' : 'Share clip'}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.6" y1="10.6" x2="15.4" y2="6.4" /><line x1="8.6" y1="13.4" x2="15.4" y2="17.6" />
          </svg>
          {isImageAsset ? 'Share' : 'Share clip'}
        </button>
      )}
    </div>
  );

  // "Add to campaign" / "Open in…" — rendered inline in the scrollable body
  // when collapsed, or moved onto the footer's line (next to Play clip) when
  // fully expanded, since the drawer is wide enough there to fit them all on
  // one row. Only one of the two spots renders this at a time (below), so the
  // single `sendToRef` outside-click-close effect still applies correctly
  // regardless of which spot is active.
  const sendToBlock = (
    <div className={styles.sendToRow} ref={sendToRef}>
      <div className={styles.sendToItem}>
        <button
          className={`${styles.sendToBtn} ${campaignMenuOpen ? styles.sendToBtnActive : ''}`}
          onClick={() => { if (!campaignMenuOpen) trackEvent(ANALYTICS_EVENTS.CAMPAIGN_MENU_OPEN, { assetId: item.id }); setCampaignMenuOpen((v) => !v); setOpenInMenuOpen(false); }}
          aria-expanded={campaignMenuOpen}
        >
          <span className={styles.sendToBtnLabel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 11l18-8-8 18-2-8-8-2z" />
            </svg>
            Add to campaign
          </span>
          <svg
            className={`${styles.sendToChevron} ${campaignMenuOpen ? styles.sendToChevronOpen : ''}`}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {campaignMenuOpen && (
          <div className={styles.sendToMenu}>
            <div className={styles.campaignList}>
              {campaigns.map((c) => (
                <button key={c.id} className={styles.campaignRow} onClick={() => handleAssignCampaign(c.id)}>
                  <span className={styles.campaignAvatar} style={{ background: c.color }}>
                    {c.avatar}
                  </span>
                  <span className={styles.campaignInfo}>
                    <span className={styles.campaignName}>{c.label}</span>
                    <span className={styles.campaignMeta}>{c.assetCount} asset{c.assetCount !== 1 ? 's' : ''} · {c.status}</span>
                  </span>
                </button>
              ))}
            </div>
            <button className={styles.newCampaignRow} onClick={() => setCampaignMenuOpen(true)}>
              <span className={styles.newCampaignIcon}>+</span>
              New campaign
            </button>
            <div className={styles.campaignCreateRow}>
              <input
                className={styles.campaignCreateInput}
                placeholder="New campaign name…"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCampaign(); }}
              />
              <button className={styles.campaignCreateBtn} disabled={!newCampaignName.trim()} onClick={handleCreateCampaign}>Add</button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.sendToItem}>
        <button
          className={`${styles.sendToBtn} ${openInMenuOpen ? styles.sendToBtnActive : ''}`}
          onClick={() => { setOpenInMenuOpen((v) => !v); setCampaignMenuOpen(false); }}
          aria-expanded={openInMenuOpen}
        >
          <span className={styles.sendToBtnLabel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Open in…
          </span>
          <svg
            className={`${styles.sendToChevron} ${openInMenuOpen ? styles.sendToChevronOpen : ''}`}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {openInMenuOpen && (
          <div className={styles.sendToMenu}>
            {OPEN_IN_APPS.map((app) => (
              <button key={app.key} className={styles.openInRow} onClick={() => handleOpenInApp(app.label)}>
                <span className={styles.openInIcon}>{app.icon}</span>
                <span className={styles.openInLabel}>{app.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className={styles.scrim} onClick={() => { trackEvent(ANALYTICS_EVENTS.DRAWER_CLOSE, { assetId: item?.id }); dispatch(closeDetailDrawer()); }} />
      <div className={`${styles.sheet} ${expanded ? styles.sheetExpanded : ''}`} role="dialog" aria-modal="true" aria-label={item.title}>
        {/* ── Header ── */}
        <div className={styles.head}>
          {/* Hidden below MIN_WIDTH_FOR_EXPAND — the expanded layout can't
              actually fit at that width, so the feature itself is hidden
              rather than offering a state that would look broken. */}
          {canExpand && (
            <button
              className={styles.expandBtn}
              onClick={() => { trackEvent(ANALYTICS_EVENTS.ACCORDION_TOGGLE, { section: 'drawer_expand', open: !expanded }); setExpanded((v) => !v); }}
              aria-label={expanded ? 'Collapse drawer' : 'Expand drawer'}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                /* Collapse — points right, back toward the drawer's pinned edge */
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 6 12 12 6 18" />
                  <polyline points="13 6 19 12 13 18" />
                </svg>
              ) : (
                /* Expand — points left, the only direction the drawer grows */
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 6 12 12 18 18" />
                  <polyline points="11 6 5 12 11 18" />
                </svg>
              )}
            </button>
          )}
          {item.category && (
            <span className={`${styles.categoryPill} ${styles[`cat_${item.category}`]}`}>
              {CATEGORY_LABEL[item.category] ?? item.category}
            </span>
          )}
          <button className={styles.closeBtn} onClick={() => { trackEvent(ANALYTICS_EVENTS.DRAWER_CLOSE, { assetId: item?.id }); dispatch(closeDetailDrawer()); }} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Top section — pinned, never scrolls. In expanded mode the
             description/AI-summary column sits to the left of the video
             (row-reverse keeps the video first in the DOM/tab order while
             appearing on the right); the video itself never resizes. ── */}
        <div className={`${styles.topSection} ${expanded ? styles.topSectionExpanded : ''}`}>
        <div className={styles.heroCol}>
        <div className={styles.heroWrap}>
          <div className={`${styles.hero} ${isImageAsset ? styles.heroImage : ''}`}>
            {isImageAsset ? (
              // Image asset — always render the actual image, never a video
              // player. Opening from a search result sets isPlaying=true (which
              // drives `started`), so without this branch an image would mount
              // an empty <video> here instead of loading the picture.
              (playbackUrl || item.thumbnail) ? (
                <img
                  src={playbackUrl || item.thumbnail}
                  className={styles.heroImgContain}
                  alt={item.title}
                />
              ) : (
                <div className={`${styles.heroPlaceholder} ${styles[`grad_${item.gradient}`]}`} />
              )
            ) : started ? (
              <video
                key={item.id}
                ref={videoRef}
                className={styles.heroVideo}
                src={playbackUrl || demoVideo}
                controls
                playsInline
                // Buffer continuously once open so seeking to a tag/transcript
                // timestamp lands inside the already-downloaded range instead of
                // kicking off a fresh request. Combined with the frozen
                // playbackUrl above, the drawer loads the file once and then
                // just seeks — clicking tags no longer re-fetches the video.
                preload="auto"
                // Mounting this element only happens as a direct result of a
                // user click (hero play button, transcript/tag seek, or
                // opening the drawer from a search result that requested
                // autoplay) — the `autoplay` attribute lets the browser start
                // playback the instant it has enough data, without depending
                // on a manual play() call inside the async onLoadedMetadata
                // handler below, which can be silently rejected by stricter
                // autoplay policies since it isn't in the gesture's call stack.
                autoPlay
                onLoadedMetadata={(e) => {
                  dispatch(setTotalSeconds(Math.floor(e.currentTarget.duration) || 0));
                  e.currentTarget.currentTime = currentSeconds;
                  if (isPlaying) e.currentTarget.play().catch(() => dispatch(setPlaying(false)));
                }}
                onTimeUpdate={(e) => {
                  dispatch(updateCurrentTime(e.currentTarget.currentTime));
                  if (shouldStopAtBoundary(e.currentTarget.currentTime, activeClipEndSeconds)) {
                    dispatch(setPlaying(false));
                    dispatch(setClipEndBoundary(null));
                  }
                }}
                onPlay={() => dispatch(setPlaying(true))}
                onPause={() => dispatch(setPlaying(false))}
              />
            ) : (
              <>
                {item.thumbnail ? (
                  <img src={item.thumbnail} className={styles.heroImg} alt={item.title} />
                ) : (
                  <div className={`${styles.heroPlaceholder} ${styles[`grad_${item.gradient}`]}`} />
                )}
                {item.duration && <span className={styles.heroDuration}>{item.duration}</span>}
                {/* Non-image only — image assets take the isImageAsset branch above. */}
                <button className={styles.heroPlay} onClick={handleHeroPlay} aria-label="Play video">
                  <span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
        </div>

          {/* Only pinned beside the video when expanded — collapsed mode
              renders this inline at the top of the scrollable body instead. */}
          {expanded && <div className={styles.bodyMain}>{descriptionBlock}</div>}
        </div>

        {/* Search bar + Share clip — same full-width spot as before (right
            above the Transcript/Insights columns), just pinned instead of
            scrolling away, when expanded. */}
        {expanded && <div className={styles.pinnedSearchRow}>{searchRowBlock}</div>}

        {/* ── Body — scrollable. ── */}
        <div className={`${styles.body} ${expanded ? styles.bodyExpanded : ''}`}>
          {/* Collapsed mode: scrolls inline with the rest of the content. */}
          {!expanded && <div className={styles.bodyMain}>{descriptionBlock}</div>}
          {!expanded && searchRowBlock}

          {/* Transcript + Insights — stacked by default, side-by-side two-column when expanded.
              Images have no transcript at all, so that column is omitted entirely. */}
          <div className={`${styles.tagsRow} ${expanded ? styles.tagsRowExpanded : ''}`}>
          {!isImageAsset && (
          <div className={styles.transcriptCol}>
          <button
            className={styles.accordionHeader}
            onClick={() => { trackEvent(ANALYTICS_EVENTS.ACCORDION_TOGGLE, { section: 'transcript', open: !transcriptOpen }); setTranscriptOpen((v) => !v); }}
            aria-expanded={transcriptOpen}
          >
            <span className={styles.accordionLabel}>Transcript</span>
            <svg
              className={`${styles.accordionChevron} ${transcriptOpen ? styles.accordionChevronOpen : ''}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {transcriptOpen && (
            detailLoading ? (
              <div className={styles.scenesList}>
                {[40, 32, 44, 28].map((w, i) => (
                  <div key={i} className={styles.skelRow}>
                    <span className={styles.skelTime} />
                    <span className={styles.skelLine} style={{ flex: 1, marginBottom: 0, width: `${w}%` }} />
                  </div>
                ))}
              </div>
            ) : filteredLines.length > 0 ? (
              <div className={styles.scenesList}>
                {filteredLines.map((line, i) => {
                  const idx = transcriptLines.indexOf(line);
                  const isActive = idx === activeLineIndex;
                  return (
                    <button
                      key={i}
                      className={`${styles.sceneRow} ${isActive ? styles.sceneRowActive : ''}`}
                      onClick={() => handleSeekLine(line.start_ms / 1000)}
                    >
                      <span className={styles.sceneTime}>{formatTimestamp(Math.floor(line.start_ms / 1000))}</span>
                      <span className={styles.sceneText}>{toDisplayText(line.text)}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className={styles.emptyText}>
                {searchLower ? 'No matching lines.' : 'No transcript available for this clip.'}
              </p>
            )
          )}
          </div>
          )}

          <div className={styles.insightsCol}>
          {/* Insights — collapsible; same tag categories as the hero section's Insights tab, filtered by the same search */}
          <button
            className={styles.accordionHeader}
            onClick={() => { trackEvent(ANALYTICS_EVENTS.ACCORDION_TOGGLE, { section: 'insights', open: !insightsOpen }); setInsightsOpen((v) => !v); }}
            aria-expanded={insightsOpen}
          >
            <span className={styles.accordionLabel}>Insights</span>
            <svg
              className={`${styles.accordionChevron} ${insightsOpen ? styles.accordionChevronOpen : ''}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {insightsOpen && tagsLoading && (
            <>
              {['Objects', 'Activities', 'Scenes'].map((label) => (
                <div key={label}>
                  <div className={styles.sectLabel}>{label}</div>
                  <div className={styles.skelChipRow}>
                    <span className={styles.skelChip} />
                    <span className={styles.skelChip} style={{ width: '90px' }} />
                    <span className={styles.skelChip} style={{ width: '56px' }} />
                  </div>
                </div>
              ))}
            </>
          )}
          {insightsOpen && !tagsLoading && TAG_CATEGORIES.map(({ label, type, key }) => {
            const allTags = assetTags?.tags?.[key] ?? [];
            const tags = searchLower
              ? allTags.filter((t) => toDisplayText(t.text).toLowerCase().includes(searchLower))
              : allTags;
            const isAdding = addingCategory === type;
            // Hide categories the search has filtered down to nothing, so
            // matching content elsewhere isn't buried under empty sections.
            if (searchLower && tags.length === 0 && !isAdding) return null;
            const categoryOpen = isCategoryOpen(type) || isAdding;
            const startAdding = () => {
              setAddingCategory(type);
              setAddValue('');
              setAddStartMs(msToTimestamp(currentSeconds * 1000));
              setEditingTag(null);
              setTagsSeeMore((prev) => ({ ...prev, [type]: true }));
            };
            return (
              <div key={type}>
                {/* Not a <button> — it wraps the quick "+ Add" button below,
                    and nesting a button inside a button is invalid HTML/breaks
                    click handling. role="button" + onKeyDown keeps the same
                    toggle behavior and keyboard accessibility. */}
                <div
                  className={styles.categoryAccordionHeader}
                  onClick={() => toggleCategory(type)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCategory(type); } }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={categoryOpen}
                >
                  <span className={styles.categoryAccordionLabel}>
                    <span className={styles.sectLabel}>{label}</span>
                    {!isAdding && (
                      <button
                        className={styles.addChipBtnInline}
                        onClick={(e) => { e.stopPropagation(); startAdding(); }}
                        aria-label={`Add ${label.toLowerCase()}`}
                        title={`Add ${label.toLowerCase()}`}
                      >+ Add</button>
                    )}
                  </span>
                  <svg
                    className={`${styles.accordionChevron} ${categoryOpen ? styles.accordionChevronOpen : ''}`}
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {categoryOpen && (
                <div
                  ref={(el) => { chipRowRefs.current[type] = el; }}
                  className={`${styles.chipRow} ${!tagsSeeMore[type] ? styles.chipRowClamped : ''}`}
                >
                  {/* Always the full list — which trailing tags stay hidden
                      while collapsed is applied imperatively (inline style,
                      not conditional rendering) by the layout effect above,
                      so every measurement pass starts from the same true
                      full set instead of chasing whatever a previous pass
                      already hid. */}
                  {tags.map((tag, i) => {
                    const chipKey = tag.id ?? `${type}-ai-${i}`;
                    const isEditingThis = editingTag?.key === chipKey;
                    if (isEditingThis) {
                      return (
                        <div key={chipKey} className={styles.tagEditBlock}>
                          <div className={styles.tagEditMainRow}>
                            <input
                              className={styles.addChipInput}
                              autoFocus
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleEditTagSave(); if (e.key === 'Escape') setEditingTag(null); }}
                            />
                            <button className={styles.addChipSave} disabled={addLoading || !editValue.trim()} onClick={handleEditTagSave}>✓</button>
                            <button className={styles.tagChipX} onClick={() => setEditingTag(null)} aria-label="Cancel edit">×</button>
                          </div>
                          <div className={styles.tagTimestampRow}>
                            <span className={styles.tagTimestampLabel}>Start</span>
                            <input
                              className={styles.tagTimestampInput}
                              placeholder="m:ss"
                              value={editStartMs}
                              onChange={(e) => setEditStartMs(e.target.value)}
                            />
                            <button
                              className={styles.tagTimestampNow}
                              onClick={() => setEditStartMs(msToTimestamp(currentSeconds * 1000))}
                              title="Use current playback time"
                            >↑ now</button>
                          </div>
                        </div>
                      );
                    }
                    const attrsTooltip = (type === 'person' || type === 'object') ? formatAttrsTooltip(tag.attrs) : null;
                    return (
                      <span
                        key={chipKey}
                        className={`${styles.tagChip}${tag.timestamp_ms != null ? ` ${styles.tagChipClickable}` : ''}`}
                        title={tag.timestamp_ms != null ? `${toDisplayText(tag.text)} — Jump to timestamp` : (attrsTooltip ?? toDisplayText(tag.text))}
                        onClick={tag.timestamp_ms != null ? () => { trackEvent(ANALYTICS_EVENTS.TAG_CLICK, { assetId: item.id, tagType: type, timestamp_ms: tag.timestamp_ms }); handleSeekLine(tag.timestamp_ms! / 1000); } : undefined}
                        role={tag.timestamp_ms != null ? 'button' : undefined}
                      >
                        {tag.timestamp_ms != null && (
                          <span className={styles.tagChipTime}>{msToTimestamp(tag.timestamp_ms)}</span>
                        )}
                        {/* Single-line + ellipsis — long scene/description text used to
                            wrap onto a 2nd line inside the pill and then get sliced off
                            mid-line by the clamped chip row's fixed height. Full text is
                            still reachable via the title tooltip above. */}
                        <span className={styles.tagChipText}>{toDisplayText(tag.text)}</span>
                        {attrsTooltip && <span className={styles.tagChipAttrs} title={attrsTooltip}>ⓘ</span>}
                        <button
                          className={styles.tagChipEdit}
                          onClick={(e) => { e.stopPropagation(); startEditTag(chipKey, type, tag); }}
                          aria-label={`Edit ${toDisplayText(tag.text)}`}
                          title="Edit"
                        >✎</button>
                        {tag.id && (
                          <button
                            className={styles.tagChipX}
                            onClick={(e) => { e.stopPropagation(); trackEvent(ANALYTICS_EVENTS.TAG_DELETE, { assetId: item.id, tagType: type }); dispatch(deleteAssetTag({ assetId: item.id, tagId: tag.id! })).then(() => dispatch(fetchAssetTags(item.id))); }}
                            disabled={!!deleteLoading[tag.id]}
                            aria-label={`Remove ${toDisplayText(tag.text)}`}
                          >×</button>
                        )}
                      </span>
                    );
                  })}
                  {/* Last item in the (possibly clamped) row itself, so it
                      wraps at the end of the visible tags instead of always
                      dropping to its own row underneath. The layout effect
                      above imperatively hides just enough trailing tag
                      elements (inline `display: none`) that this always
                      lands inside the clamp instead of on a clipped 3rd
                      line. */}
                  {tagsOverflow[type] && (
                    <button
                      className={styles.seeMoreBtn}
                      onClick={() => setTagsSeeMore((prev) => ({ ...prev, [type]: !prev[type] }))}
                    >
                      {tagsSeeMore[type] ? 'See less' : 'See more…'}
                    </button>
                  )}
                </div>
                )}
                {categoryOpen && (
                  <div className={styles.chipRowFooter}>
                    {tags.length === 0 && !isAdding && <span className={styles.emptyTagText}>None yet</span>}
                    {isAdding && (
                      <div className={styles.tagEditBlock}>
                        <div className={styles.tagEditMainRow}>
                          <input
                            className={styles.addChipInput}
                            autoFocus
                            value={addValue}
                            placeholder={`Add ${label.toLowerCase()}…`}
                            onChange={(e) => setAddValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleAddTag(type); if (e.key === 'Escape') setAddingCategory(null); }}
                          />
                          <button className={styles.addChipSave} disabled={addLoading || !addValue.trim()} onClick={() => handleAddTag(type)}>Add</button>
                          <button className={styles.tagChipX} onClick={() => setAddingCategory(null)} aria-label="Cancel">×</button>
                        </div>
                        <div className={styles.tagTimestampRow}>
                          <span className={styles.tagTimestampLabel}>Start</span>
                          <input
                            className={styles.tagTimestampInput}
                            placeholder="m:ss"
                            value={addStartMs}
                            onChange={(e) => setAddStartMs(e.target.value)}
                          />
                          <button
                            className={styles.tagTimestampNow}
                            onClick={() => setAddStartMs(msToTimestamp(currentSeconds * 1000))}
                            title="Use current playback time"
                          >↑ now</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
          </div>

          {/* Notes */}
          <div className={styles.accordionLabel} style={{ margin: '20px 0 10px' }}>Notes</div>
          <div className={styles.notesList}>
            {tagsLoading && <div className={styles.skelLine} style={{ width: '70%' }} />}
            {!tagsLoading && notes.map((n, i) => (
              <div key={n.id ?? i} className={styles.noteRow}>
                <p className={styles.noteText}>{toDisplayText(n.text)}</p>
                {n.id && (
                  <button
                    className={styles.noteDelete}
                    onClick={() => { trackEvent(ANALYTICS_EVENTS.NOTE_DELETE, { assetId: item.id }); dispatch(deleteAssetTag({ assetId: item.id, tagId: n.id! })).then(() => dispatch(fetchAssetTags(item.id))); }}
                    disabled={!!deleteLoading[n.id]}
                    aria-label="Remove note"
                  >×</button>
                )}
              </div>
            ))}
          </div>
          <div className={styles.noteAddRow}>
            <input
              className={styles.noteInput}
              value={noteText}
              placeholder="Add a note about this cut…"
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddNote(); }}
            />
            <button className={styles.noteAddBtn} disabled={addLoading || !noteText.trim()} onClick={handleAddNote}>Add</button>
          </div>

          {/* Send to — moved onto the footer's line (next to Play clip) when
              fully expanded; see `sendToBlock` and the footer below. */}
          {!expanded && (
            <>
              <div className={styles.sectLabel}>Send to</div>
              {sendToBlock}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={styles.foot}>
          <div className={styles.footLeft}>
            {!isImageAsset && (
              <button className={`${styles.playBtn} ${expanded ? styles.playBtnExpanded : ''}`} onClick={handleFootPlay}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                {started && isPlaying ? 'Pause' : 'Play clip'}
              </button>
            )}
            {/* "Add to campaign" / "Open in…" only fit on this line once the
                drawer is fully expanded — otherwise they stay in the body,
                below "Send to" (see above). */}
            {expanded && sendToBlock}
          </div>
          <div className={styles.footIcons}>
            <button className={styles.iconBtn} onClick={() => { trackEvent(ANALYTICS_EVENTS.DOWNLOAD_CLICK, { assetId: item.id }); setDownloadOpen(true); }} aria-label="Download" title="Download">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
            <button className={styles.iconBtn} onClick={handleCopyLink} aria-label="Copy link" title="Copy link">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {shareOpen && (
        <ShareModal url={storageUrl || window.location.href} title={item.title} onClose={() => setShareOpen(false)} />
      )}
      {downloadOpen && (
        <DownloadModal
          url={downloadUrl}
          fileName={`${item.title}.mp4`}
          durationMs={detail?.duration_ms}
          onClose={() => setDownloadOpen(false)}
        />
      )}
    </>
  );
}
